/**
 * I don't like needing Vue to update the validator hash
 * but it does get the components' +isValid+ function to run whenever a validator is added/removed
 */
import Vue from "vue"
import Validator from './validator'
import {Hash} from '~/interfaces/hashInterfaces'

/**
 * Simple class used for grouping Validators together to "act as one".
 * Original usage is to group all Validators for a FormElement so determination of FormElement
 * validity need only be asked once of the ValidatorGroup
 */
export default class ValidatorGroup<T extends Validator<unknown> = Validator<unknown>> {
  private validators: Hash<T> = {}

  find(id: string): T {
    return this.validators[id]
  }

  /**
   * Group is valid if all Validators are valid
   */
  get isValid(): boolean {
    for (const key in this.validators) {
      const v = this.validators[key]
      if (!v.isValid) {
        return false
      }
    }
    return true
  }

  /**
   * Gather all error messages from validators
   */
  get errorMessages(): Array<string> {
    const messages = []
    for (const key in this.validators) {
      const v = this.validators[key]
      if (!v.isValid) messages.push(v.errorMessage)
    }
    return messages
  }

  /**
   * Add the Validator to the hash of Validators where its +id+ will be used as the hash key.
   * @param validator
   */
  register(validator: T): void {
    if (validator.registered) return
    Vue.set(this.validators, validator.id, validator)
    validator.registered = true
  }

  /**
   * Remove a Validator from the hash of Validators based on the +id+ provided.
   * @param id
   */
  unregister(id: string): void {
    const validator = this.validators[id]
    if (!validator?.registered) return

    Vue.delete(this.validators, id)
    validator.registered = false
  }

  /**
   * Remove all Validators from the hash of Validators
   */
  unregisterAll(): void {
    for (const key in this.validators) {
      this.unregister(key)
    }
  }

  /**
   * Run each validator
   */
  validate(...args): void {
    for (const key in this.validators) {
      const validator = this.validators[key]
      validator.validate(...args)
    }
  }
}